import type { GeneratedAsset, GeneratedAssetTool } from '@pulsify/shared/dto'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'

export const CHECKERBOARD =
  'bg-[repeating-conic-gradient(var(--color-muted)_0_25%,var(--color-card)_0_50%)] bg-size-[16px_16px]'

const TOOL_LABELS: Record<GeneratedAssetTool, () => string> = {
  format: m.mediaLibraryToolFormat,
  crop: m.mediaLibraryToolCrop,
  filter: m.mediaLibraryToolFilter,
  edit: m.mediaLibraryToolEdit,
  refine: m.mediaLibraryToolRefine,
  event: m.mediaLibraryToolEvent,
  qr: m.mediaLibraryToolQr,
  flyer: m.mediaLibraryToolFlyer,
  menu: m.mediaLibraryToolMenu,
  social: m.mediaLibraryToolSocial,
}

export function toolLabel(tool: GeneratedAssetTool): string {
  return TOOL_LABELS[tool]()
}

export function assetTitle(asset: GeneratedAsset): string {
  return asset.title.trim() || m.mediaLibraryUntitled()
}

export function isImageAsset(asset: GeneratedAsset): boolean {
  return asset.mimeType.startsWith('image/')
}

export function fileTypeLabel(mimeType: string): string {
  const subtype = mimeType.split('/')[1]?.split('+')[0]
  return subtype ? subtype.toUpperCase() : mimeType
}

function fileExtension(mimeType: string): string {
  const subtype = mimeType.split('/')[1]?.split('+')[0] ?? 'bin'
  return subtype === 'jpeg' ? 'jpg' : subtype
}

export function formatBytes(bytes: number): string {
  const megabytes = bytes / (1024 * 1024)
  const [value, unit] = megabytes >= 1 ? [megabytes, 'megabyte'] : [bytes / 1024, 'kilobyte']
  return new Intl.NumberFormat(getLocale(), {
    style: 'unit',
    unit,
    maximumFractionDigits: value < 10 ? 1 : 0,
  }).format(value)
}

export function formatDimensions(asset: GeneratedAsset): string | null {
  if (asset.width == null || asset.height == null) return null
  const number = new Intl.NumberFormat(getLocale())
  return `${number.format(asset.width)} × ${number.format(asset.height)} px`
}

export function formatCreatedAt(iso: string, style: 'short' | 'long'): string {
  const date = new Date(iso)
  return style === 'short'
    ? date.toLocaleDateString(getLocale(), { dateStyle: 'medium' })
    : date.toLocaleString(getLocale(), { dateStyle: 'long', timeStyle: 'short' })
}

export function downloadFilename(asset: GeneratedAsset): string {
  const base = (asset.title.trim() || asset.tool).replace(/[\\/:*?"<>|]+/g, '-').slice(0, 80)
  return `${base}.${fileExtension(asset.mimeType)}`
}
